import { Text, Input, Button, Checkbox, Flex, Form, Label, Grid } from '@pega/cosmos-react-core';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import { appState } from '../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import CountryCodeAutoComplete from '../../Utilities/CountryCodeAutoComplete';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import CreateVonage from '../Vonage/CreateVonage';
import { addNewSMSAccount } from './SMSApis';
import DisplayVonageTwilioOptions from '../../Utilities/DisplayVonageTwilioOptions';

interface SMSFormData {
  accountSid: string;
  authToken: string;
  name: string;
  phoneNumber: string;
}

const CreateSMS: React.FC = () => {
  const { register, handleSubmit, errors, reset, formState } = useForm<SMSFormData>({ mode: 'all' });
  const [countryCodes, setCountryCodes] = useState<{ [country: string]: string }>({});
  const sessionId = useSelector((state: appState) => state.sessionId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const [createSMSType, setcreateSMSType] = useState('twilio');
  const [outboundMessagingEnabled, setOutboundMessagingEnabled] = useState(false);

  const onSubmit = async (data: SMSFormData) => {
    const payload = {
      account_sid: data.accountSid,
      auth_token: data.authToken,
      name: data.name,
      phone_number: data.phoneNumber,
      country_codes: countryCodes,
      outbound_messaging_enabled: outboundMessagingEnabled,
    };
    const triggerAPI = Object.keys(countryCodes).length > 0 && formState.touched;
    if (triggerAPI) {
      dispatch(updateLoaderDetails(true));
      const json = await addNewSMSAccount(sessionId, payload);
      dispatch(updateLoaderDetails(false));
      if (json?.status === 200) {
        reset();
        navigate(`/twilio/${json?.data?.connectionId}`);
        dispatch(updateBannerInfo(`${translate('SMS')} ${translate('account_registered')}`, 'success'));
      } else if (json?.status === 403) {
        navigate('/');
        dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      } else {
        let errorMessage;
        if (json?.data?.code) {
          errorMessage = translate(json.data.code);
        }
        dispatch(updateBannerInfo(errorMessage || translate('sms_creation_failure'), 'urgent'));
      }
    }
  };

  const actions = (
    <Flex>
      <Button name="Save" type="submit" variant="primary">
        {translate('save')}
      </Button>
    </Flex>
  );

  const headerElement = () => {
    return (
      <Text variant="h1" as="h1">
        {`${translate('new')} ${translate('SMS')}`}
      </Text>
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      {process.env.REACT_APP_ENABLE_VONAGE === '1' && (
        <DisplayVonageTwilioOptions
          createAccountVia={createSMSType}
          setAccountType={setcreateSMSType}
          accountType="create_sms_type"
        />
      )}
      <Grid
        container={{
          gap: 2,
        }}
      >
        {createSMSType === 'twilio' ? (
          <Form actions={actions} className="basic-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container={{ gap: 2 }} style={{ width: '50%' }}>
              <Input
                name="name"
                label={translate('name')}
                required={true}
                autoComplete="off"
                info={errors.name?.message}
                status={errors.name?.message ? 'error' : undefined}
                ref={register({ required: translate('name_empty') })}
              />

              <Input
                name="phoneNumber"
                type="text"
                label={translate('number')}
                autoComplete="off"
                placeholder="+1 555 555 5555"
                info={errors.phoneNumber?.message}
                required={true}
                status={errors.phoneNumber?.message ? 'error' : undefined}
                ref={register({
                  required: translate('phone_number_criteria_error'),
                  pattern: { value: /^[+]{1}[\s0-9]*$/, message: translate('phone_number_criteria_error_2') },
                })}
              />
              <Input
                name="accountSid"
                label={translate('account_sid')}
                autoComplete="off"
                type="text"
                info={errors.accountSid?.message}
                required={true}
                status={errors.accountSid?.message ? 'error' : undefined}
                ref={register({
                  required: translate('account_sid_criteria_error'),
                  pattern: { value: /^\w*$/, message: translate('account_sid_criteria_error_1') },
                })}
              />
              <Input
                name="authToken"
                label={translate('auth_token')}
                autoComplete="off"
                info={errors.authToken?.message}
                required={true}
                status={errors.authToken?.message ? 'error' : undefined}
                ref={register({ required: translate('auth_token_empty') })}
              />
              {process.env.REACT_APP_ENABLE_OUTBOUND_MESSAGING_FOR_SMS && (
                <Checkbox
                  label={translate('enable_outbound_messaging')}
                  name="outboundMessagingEnabled"
                  checked={outboundMessagingEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setOutboundMessagingEnabled(event.target.checked)
                  }
                />
              )}
            </Grid>

            <Flex
              container={{
                direction: 'column',
                itemGap: 1,
              }}
            >
              <Label>{translate('webhook')}</Label>
              <Text>{process.env.REACT_APP_INCOMING_WEBHOOK}/twilio</Text>
            </Flex>
            <CountryCodeAutoComplete countryCodes={countryCodes} setCountryCodes={setCountryCodes} />
          </Form>
        ) : (
          <Grid container={{ gap: 2 }} style={{ width: '50%' }}>
            <CreateVonage type="sms" />
          </Grid>
        )}
      </Grid>
    </SectionRenderTemplate>
  );
};

export default CreateSMS;
